import { YEARLY_PERIODICITY } from "@arbeidsark/shared"
import type { SharedStripeSetupIntent } from "@arbeidsark/shared"
import type { SubscriptionPeriodicity } from "@arbeidsark/shared"
import { Elements } from "@stripe/react-stripe-js"
import type { StripeElementsOptions } from "@stripe/stripe-js"
import { useEffect, useState } from "react"
import styled, { useTheme } from "styled-components"
import { Loader } from "~/components"
import { useLanguage } from "~/locale/TranslationProvider"
import { useTranslate } from "~/ur-react-hooks"
import { stripeLocaleLookup } from "~/utils/stripe"
import { trpc } from "~/utils/trpc"
import { stripePromise } from "../stripe"
import SelectSubscription from "./SelectSubscription"
import SetupEnterPaymentDetails from "./SetupEnterPaymentDetails"

const Layout = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`

const Card = styled.div`
  background: white;
  box-shadow: ${(props) => props.theme.shadow.elevation3};

  padding: 2rem 1.5rem;

  border-radius: 8px;

  width: 378px;
`

const CenterSpinner = styled.div`
  display: flex;
  justify-content: center;
`

export type SelectSubscriptionAndPayProps = {}

const TrialPay: React.FC<SelectSubscriptionAndPayProps> = () => {
  const [step, setStep] = useState<"select" | "pay">("select")
  const [periodicity, setPeriodicity] =
    useState<SubscriptionPeriodicity>(YEARLY_PERIODICITY)

  const { mutate: createSetupIntent, isPending: isLoading } =
    trpc.subscriptions.createSetupIntentForSubscription.useMutation({
      onSuccess(setupIntent) {
        setSetupIntent(setupIntent)
      },
    })

  const { mutate: changePeriodicity } =
    trpc.subscriptions.changeUserSubscriptionPeriodicity.useMutation()

  const [language] = useLanguage()
  const theme = useTheme()

  const [setupIntent, setSetupIntent] = useState<
    SharedStripeSetupIntent | undefined
  >()

  useEffect(() => {
    if (!setupIntent) {
      createSetupIntent()
    }
  }, [createSetupIntent, setupIntent])

  const translations = useTranslate({
    enterPaymentTitle: "pay-for-subscription.title",
    enterPaymentSubmitText: "pay-for-subscription.purchase",
  })

  if (!setupIntent) {
    return (
      <Layout>
        <Card>
          <CenterSpinner>
            <Loader.Spinner />
          </CenterSpinner>
        </Card>
      </Layout>
    )
  }

  const options: StripeElementsOptions = {
    clientSecret: setupIntent.clientSecret,
    appearance: {
      rules: {
        ".Input": {
          borderColor: theme.colors.gray70,
          borderRadius: "4px",
          outline: "none",
        },
        ".Input:focus": {
          border: "2px solid " + theme.colors.primary40,
          boxShadow: "none",
        },
      },
    },
    locale: stripeLocaleLookup[language],
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <Layout>
        <Card>
          {step === "select" && (
            <SelectSubscription
              onNext={() => {
                setStep("pay")
                changePeriodicity(periodicity)
              }}
              value={periodicity}
              onChange={setPeriodicity}
            />
          )}
          {step === "pay" && isLoading && <Loader.Spinner />}
          {step === "pay" && !isLoading && (
            <SetupEnterPaymentDetails
              title={translations.enterPaymentSubmitText}
              submitText={translations.enterPaymentSubmitText}
              redirectUrl={`${
                import.meta.env.VITE_APP_URL
              }/subscription/setup-success`}
              onBack={() => {
                setStep("select")
              }}
            />
          )}
        </Card>
      </Layout>
    </Elements>
  )
}

export default TrialPay
